/**
 * single-quantity-cart.js
 *
 * The shop only sells digital STL files, so a customer never needs more than one
 * copy of a given product. This script enforces a hard limit of one unit per
 * product across the whole storefront:
 *
 *  - Any "Add to cart" button (product pages, collection quick-add, quick-add
 *    modal, product recommendations, …) is disabled as soon as its product is in
 *    the cart, and shows the "Added" state instead.
 *  - The button re-enables automatically if the product is later removed from the
 *    cart (no page reload required).
 *
 * The cart line quantity is additionally capped at 1 server-side (see
 * snippets/quantity-selector.liquid), so this script only manages the add buttons.
 *
 * It relies on the theme's bubbling "cart:update" event (see assets/events.js)
 * and re-reads the authoritative cart state from /cart.js after every change.
 */
(function () {
  'use strict';

  var ROOT = '/';
  try {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.root) {
      ROOT = Theme.routes.root;
    }
  } catch (e) {
    /* Theme global not available yet – fall back to '/'. */
  }

  /** Product IDs (as strings) currently present in the cart. */
  var inCart = new Set();
  var refreshScheduled = false;

  /**
   * Resolve the product id associated with an add-to-cart button.
   * @param {Element} btn
   * @returns {string|null}
   */
  function productIdFor(btn) {
    if (btn.dataset && btn.dataset.productId) return String(btn.dataset.productId);

    var component = btn.closest('add-to-cart-component');
    if (component && component.dataset.productId) return String(component.dataset.productId);

    var form = btn.closest('product-form-component');
    if (form && form.dataset.productId) return String(form.dataset.productId);

    return null;
  }

  /**
   * Only buttons that are otherwise addable (i.e. not sold out) are managed.
   * Sold-out buttons keep their own disabled state untouched.
   * @param {Element} btn
   */
  function isManageable(btn) {
    return btn.dataset && btn.dataset.canAdd === 'true';
  }

  /**
   * @param {Element} btn
   * @param {boolean} locked - Whether the product is already in the cart.
   */
  function setButtonState(btn, locked) {
    if (!isManageable(btn)) return;

    if (locked) {
      btn.classList.add('is-in-cart');
      btn.setAttribute('disabled', 'true');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.classList.remove('is-in-cart');
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    }
  }

  /** Apply the current cart state to every add-to-cart button on the page. */
  function applyAll() {
    var buttons = document.querySelectorAll('[data-single-qty]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var pid = productIdFor(btn);
      if (!pid) continue;
      setButtonState(btn, inCart.has(pid));
    }
  }

  /** Read the authoritative cart contents and refresh all buttons. */
  function refresh() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js request failed');
        return res.json();
      })
      .then(function (cart) {
        inCart.clear();
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          inCart.add(String(items[i].product_id));
        }
        applyAll();
      })
      .catch(function () {
        /* Keep the last known (server-rendered) state on failure. */
      });
  }

  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    setTimeout(function () {
      refreshScheduled = false;
      refresh();
    }, 150);
  }

  // The theme fires a bubbling "cart:update" event on add, remove and quantity
  // change (see CartAddEvent / CartUpdateEvent in assets/events.js).
  document.addEventListener('cart:update', scheduleRefresh);

  // Optimistically lock a button the moment it is clicked so a fast double-click
  // cannot add a second copy before the cart round-trip completes. The lock is
  // applied on the next tick so the form submission still goes through, and is
  // reconciled by refresh() (which re-enables it if the add ultimately failed).
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var btn = target.closest('[data-single-qty]');
    if (!btn || !isManageable(btn)) return;

    if (btn.hasAttribute('disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    setTimeout(function () {
      setButtonState(btn, true);
    }, 0);
  });

  // Re-apply state when new add-to-cart buttons are injected (quick-add modal,
  // morphed sections, product recommendations, infinite scroll, …).
  function observe() {
    if (typeof MutationObserver === 'undefined' || !document.body) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('[data-single-qty]')) ||
            (node.querySelector && node.querySelector('[data-single-qty]'))
          ) {
            applyAll();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    observe();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
